import React, { useEffect, useState } from "react";
import axios from "../../axiosConfig";
import FinancialsGrid from "./FinancialsGrid";
import SharePriceChart from "./SharePriceChart";
import { REQUIRED_SELECTED } from "./financialFormat";
import "./CompanyCard.css";


// A value with its currency symbol (or code) before or after it, as currencies.preorpost says
const formatMoney = (value, currency, { millions = false } = {}) => {
  if (value === null || value === undefined) return "n/a";
  const number = millions
    ? `${(value / 1000000).toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}m`
    : value.toLocaleString("en-GB", { maximumFractionDigits: 2 });
  const symbol = currency?.symbol || currency?.code || "";
  return currency?.preorpost === "R" ? `${number}${symbol}` : `${symbol}${number}`;
};

const CompanyCard = ({
  user,
  companyId,
  ticker,
  name,
  displayName,
  files,
  benchmarkFiles = [],
  model,
  benchmark = false,
  financialsTable = null,
  financials = [],
  yearEnd,
  fetchCompanies,
  fetchPendingTasks,
  aiPromptFields = {},
}) => {
  const [creatingFile, setCreatingFile] = useState(false);
  const [creatingNewFile, setCreatingNewFile] = useState(false);
  const [newCreateModalVisible, setNewCreateModalVisible] = useState(false);
  const [newCreateForecast, setNewCreateForecast] = useState("one_year");
  const [newRefreshSelectedFile, setNewRefreshSelectedFile] = useState("");
  const [newRefreshSubmitting, setNewRefreshSubmitting] = useState(false);
  const [newRefreshModalVisible, setNewRefreshModalVisible] = useState(false);
  const [refreshFinancials, setRefreshFinancials] = useState(false);
  const [advancedVisible, setAdvancedVisible] = useState(false);
  const [financialsDirty, setFinancialsDirty] = useState(false);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState("");
  const [aiGuidanceModalVisible, setAiGuidanceModalVisible] = useState(false);
  const defaultGuidance = {
    general: "",
    title: "",
    boldpara: "",
    bullet1: "",
    bullet2: "",
    bullet3: "",
    bullet4: "",
  };
  const [aiGuidance, setAiGuidance] = useState(defaultGuidance);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [aiSubmitting, setAiSubmitting] = useState(false);
  const [aiContent, setAiContent] = useState({
    title: "",
    boldpara: "",
    bullet1: "",
    bullet2: "",
    bullet3: "",
    bullet4: "",
    comment: "",
  });
  const [refreshingFile, setRefreshingFile] = useState(null);
  const [checkingFile, setCheckingFile] = useState(null);
  const [preparingFile, setPreparingFile] = useState(null);
  const [releasingFile, setReleasingFile] = useState(null);
  const [addingInsider, setAddingInsider] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [refreshModalVisible, setRefreshModalVisible] = useState(false);
  const [checkModalVisible, setCheckModalVisible] = useState(false);
  const [prepareModalVisible, setPrepareModalVisible] = useState(false);
  const [releaseModalVisible, setReleaseModalVisible] = useState(false);
  const [insideModalVisible, setInsideModalVisible] = useState(false);
  const [includeQuestion, setIncludeQuestion] = useState(false);
  const [addBy, setAddBy] = useState("");
  const now = new Date();
  const [addTime, setAddTime] = useState(
    new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16),
  );
  const [selectedFile, setSelectedFile] = useState(null);

  const timeAgo = (dateString) => {
    if (!dateString) return "Unknown time";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    // Convert both times to UTC milliseconds
    const diffMs = now.getTime() - date.getTime();

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years} year${years > 1 ? "s" : ""} ago`;
    if (months > 1) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (weeks > 0) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} min${minutes !== 1 ? "s" : ""} ago`;
    return `Less than a minute ago`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";

    const date = new Date(dateString);
    const now = new Date();

    // Helper to format time like "HH:MM"
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Helper to get difference in days between two dates (ignoring time)
    const diffDays = (d1, d2) => {
      const oneDay = 24 * 60 * 60 * 1000;
      const date1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
      const date2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
      return Math.round((date2 - date1) / oneDay);
    };

    const daysDiff = diffDays(date, now);

    // Helper to format day without leading zero
    const dayWithoutLeadingZero = date.getDate(); // getDate() returns number without leading zero
    const monthShort = date.toLocaleDateString(undefined, { month: "short" }); // e.g. May

    if (daysDiff === 0) {
      // Today: show only time
      return timeStr;
    } else if (daysDiff === 1) {
      // Yesterday
      return `${timeStr} yesterday`;
    } else if (daysDiff > 1 && daysDiff < 7) {
      // Within the last week
      const dayName = date.toLocaleDateString(undefined, { weekday: "long" }); // e.g. Monday
      return `${timeStr} on ${dayName}`;
    } else if (daysDiff >= 7) {
      // Older than a week
      const isThisYear = date.getFullYear() === now.getFullYear();

      if (isThisYear) {
        // This year
        return `${timeStr} on ${dayWithoutLeadingZero} ${monthShort}`;
      } else {
        // Different year
        const year = date.getFullYear();
        return `${timeStr} on ${dayWithoutLeadingZero} ${monthShort} ${year}`;
      }
    }

    return "Invalid date";
  };

  const handleCreate = () => {
    setCreateModalVisible(true);
    setIncludeQuestion(false);
    setAddBy("");
    const now = new Date();
    setAddTime(
      new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16),
    );
  };

  const handleConfirmCreate = async () => {
    setCreatingFile(true);
    try {
      const payload = {
        company_id: companyId,
        addBy: includeQuestion ? addBy : "",
        addTime: includeQuestion ? addTime : "",
      };
      const response = await axios.post("/api/create/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      alert(`Failed to send CREATE request for ${displayName || name}`);
    } finally {
      setCreatingFile(false);
      setCreateModalVisible(false);
    }
  };

  const handleNewCreate = () => {
    setNewCreateForecast("one_year");
    setNewCreateModalVisible(true);
  };

  const handleConfirmNewCreate = async () => {
    setCreatingNewFile(true);
    try {
      const payload = { company_id: companyId, forecast: newCreateForecast };
      const response = await axios.post("/api/new-create/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
      setNewCreateModalVisible(false);
    } catch (error) {
      const msg = error.response?.data?.error;
      alert(msg || `Failed to send NEW CREATE request for ${displayName || name}`);
    } finally {
      setCreatingNewFile(false);
    }
  };

  const handleNewRefresh = () => {
    setNewRefreshSelectedFile(benchmarkFiles[0]?.filename ?? "");
    setRefreshFinancials(false);
    setNewRefreshModalVisible(true);
  };

  const handleConfirmNewRefresh = async () => {
    setNewRefreshSubmitting(true);
    try {
      const payload = {
        company_id: companyId,
        fileName: newRefreshSelectedFile,
        refresh_financials: refreshFinancials,
      };
      const response = await axios.post("/api/new-refresh/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
      setNewRefreshModalVisible(false);
    } catch (error) {
      const msg = error.response?.data?.error;
      alert(msg || `Failed to send NEW REFRESH request for ${displayName || name}`);
    } finally {
      setNewRefreshSubmitting(false);
    }
  };

  const handleGuidanceChange = (e) => {
    const { name: field, value } = e.target;
    setAiGuidance((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateAI = async () => {
    localStorage.setItem(`ai_guidance_${ticker}`, JSON.stringify(aiGuidance));
    setAiLoading(true);
    try {
      const { general, ...fieldOverrides } = aiGuidance;
      const response = await axios.post(
        "/api/fetch-ai/",
        {
          company_id: companyId,
          general,
          field_overrides: fieldOverrides,
        },
        { withCredentials: true },
      );
      setAiContent(response.data);
      setAiGuidanceModalVisible(false);
      setAiModalVisible(true);
    } catch (error) {
      const msg = error.response?.data?.error;
      alert(msg || `Failed to fetch AI content for ${displayName || name}`);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiChange = (e) => {
    const { name: field, value } = e.target;
    setAiContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirmCreateAI = async () => {
    setAiSubmitting(true);
    try {
      const { comment, ...noteFields } = aiContent;
      const payload = { company_id: companyId, ...noteFields };
      const response = await axios.post("/api/create-ai/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
      setAiModalVisible(false);
    } catch (error) {
      alert(`Failed to start CREATE AI task for ${displayName || name}`);
    } finally {
      setAiSubmitting(false);
    }
  };

  const handleRefresh = (file) => {
    setSelectedFile(file);
    setRefreshFinancials(false);
    setRefreshModalVisible(true);
  };

  const handleConfirmRefresh = async () => {
    setRefreshingFile(selectedFile.filename);
    try {
      // Benchmark notes live in the Benchmark folder and are refreshed in place
      const response = benchmark
        ? await axios.post(
            "/api/new-refresh/",
            {
              company_id: companyId,
              fileName: selectedFile.filename,
              refresh_financials: refreshFinancials,
            },
            { withCredentials: true },
          )
        : await axios.post(
            "/api/refresh/",
            { fileName: selectedFile.filename, company_id: companyId },
            { withCredentials: true },
          );
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      const msg = error.response?.data?.error;
      alert(msg || `Failed to send REFRESH request for ${selectedFile.filename}`);
    } finally {
      setRefreshingFile(null);
      setRefreshModalVisible(false);
    }
  };

  const handleCheck = (file) => {
    setSelectedFile(file);
    setCheckModalVisible(true);
  };

  const handleConfirmCheck = async () => {
    setCheckingFile(selectedFile.filename);
    try {
      const payload = { fileName: selectedFile.filename, company_id: companyId };
      const response = await axios.post("/api/check/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      alert(`Failed to send CHECK request for ${selectedFile.filename}`);
    } finally {
      setCheckingFile(null);
      setCheckModalVisible(false);
    }
  };

  const handlePrepare = (file) => {
    setSelectedFile(file);
    setPrepareModalVisible(true);
  };

  const handleConfirmPrepare = async (file) => {
    setPreparingFile(selectedFile.filename);
    try {
      // Benchmark notes are prepared by note_prepare instead of the VBA macro
      const response = benchmark
        ? await axios.post(
            "/api/new-prepare/",
            { company_id: companyId, fileName: selectedFile.filename },
            { withCredentials: true },
          )
        : await axios.post(
            "/api/prepare/",
            { fileName: selectedFile.filename, company_id: companyId },
            { withCredentials: true },
          );
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      alert(`Failed to send PREPARE request for ${selectedFile.filename}`);
    } finally {
      setPreparingFile(null);
      setPrepareModalVisible(false);
    }
  };

  const handleRelease = (file) => {
    setSelectedFile(file);
    setReleaseModalVisible(true);
  };

  const handleConfirmRelease = async (file) => {
    setReleasingFile(selectedFile.filename);
    try {
      const payload = { fileName: selectedFile.filename, company_id: companyId };
      // Benchmark notes are released by note_release instead of the VBA macro
      const response = await axios.post(
        benchmark ? "/api/new-release/" : "/api/release/",
        payload,
        { withCredentials: true },
      );
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      const msg = error.response?.data?.error;
      alert(msg || `Failed to send RELEASE request for ${selectedFile.filename}`);
    } finally {
      setReleasingFile(null);
      setReleaseModalVisible(false);
    }
  };

  const handleInside = () => {
    setInsideModalVisible(true);
    setAddBy("");
    const now = new Date();
    setAddTime(
      new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16),
    );
  };

  const handleConfirmInside = async () => {
    setAddingInsider(true);
    try {
      const payload = {
        company_id: companyId,
        addBy,
        addTime,
      };
      const response = await axios.post("/api/inside/", payload, {
        withCredentials: true,
      });
      if (response.data.task_id) fetchPendingTasks();
    } catch (error) {
      alert(`Failed to send INSIDE request for ${ticker}`);
    } finally {
      setAddingInsider(false);
      setInsideModalVisible(false);
    }
  };

  // Share price, market cap and the share price history, fetched each time Advanced opens
  useEffect(() => {
    if (!advancedVisible) return;
    let cancelled = false;
    setOverview(null);
    setOverviewError("");
    axios
      .get("/api/company/overview/", { params: { company_id: companyId }, withCredentials: true })
      .then((response) => !cancelled && setOverview(response.data))
      .catch((error) => {
        if (!cancelled) {
          setOverviewError(error.response?.data?.error || "Couldn't load the share price data.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [advancedVisible, companyId]);

  const chartTitle = `Share price${overview?.trading_currency?.code ? ` (${overview.trading_currency.code})` : ""}`;

  const closeAdvanced = () => {
    if (
      financialsDirty &&
      !window.confirm("Discard your unsaved changes to the financials table?")
    ) {
      return;
    }
    setAdvancedVisible(false);
    setFinancialsDirty(false);
  };

  // Periods selected for the note in the saved financials table
  const selectedPeriods =
    financialsTable?.periods.filter((p) => p.selected).length ?? 0;
  const newCreateShortBy = REQUIRED_SELECTED[newCreateForecast] - selectedPeriods;
  const refreshFinancialsBlocked =
    refreshFinancials && selectedPeriods < REQUIRED_SELECTED.one_year;

  // "Refresh financial table" option of the New Refresh modals
  const renderRefreshFinancialsOption = () =>
    financialsTable && (
      <div className="question-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={refreshFinancials}
            onChange={(e) => setRefreshFinancials(e.target.checked)}
          />
          Refresh financial table
        </label>
        {refreshFinancials && (
          <small className={refreshFinancialsBlocked ? "form-error" : "form-hint"}>
            {refreshFinancialsBlocked
              ? `Select at least ${REQUIRED_SELECTED.one_year} periods in Advanced first (${selectedPeriods} selected).`
              : `${selectedPeriods} periods selected. A two-year note needs ${REQUIRED_SELECTED.two_years}.`}
          </small>
        )}
      </div>
    );

  // The file list, shown on the card and in the Advanced modal
  const renderFiles = () => (
    <>
      {files.length > 0 ? (
        <ul>
          {files
            // Sort by most recent
            .sort(
              (a, b) => new Date(b.last_modified) - new Date(a.last_modified),
            )
            .slice(0, showMore ? files.length : 1)
            .map((file, index) => {
              // Benchmark notes' tasks are "NEW PREPARE" etc.; treat them like the normal ones
              const taskType = file.prev_task_type?.replace(/^NEW /, "");
              const backgroundClass =
                taskType === "REFRESH"
                  ? "bg-refresh"
                  : taskType === "CHECK"
                    ? "bg-check"
                    : taskType === "PREPARE"
                      ? "bg-prepare"
                      : taskType === "RELEASE"
                        ? "bg-release"
                        : "";

              return (
                <li key={index} className={backgroundClass}>
                  <div className="file-item">
                    <strong>{file.filename.replace(".docx", "")}</strong>
                    {file.prev_task_type && (
                      <small>
                        <i>
                          ({taskType} -{" "}
                          {timeAgo(file.prev_task_time)})
                        </i>
                      </small>
                    )}
                    <small>
                      Last Modified: {formatDate(file.last_modified)} (
                      {timeAgo(file.last_modified)})
                    </small>
                    <button
                      onClick={() => handleRefresh(file)}
                      disabled={refreshingFile === file.filename}
                    >
                      {refreshingFile === file.filename
                        ? "Refreshing..."
                        : "Refresh"}
                    </button>
                    {user?.is_management && !benchmark && (
                      <button
                        onClick={() => handleCheck(file)}
                        disabled={checkingFile === file.filename}
                      >
                        {checkingFile === file.filename
                          ? "Checking..."
                          : "Check"}
                      </button>
                    )}
                    {user?.is_management && (
                      <button
                        onClick={() => handlePrepare(file)}
                        disabled={
                          preparingFile === file.filename ||
                          (!benchmark &&
                            taskType !== "CHECK" &&
                            taskType !== "PREPARE")
                        }
                      >
                        {preparingFile === file.filename
                          ? "Preparing..."
                          : "Prepare"}
                      </button>
                    )}
                    {user?.is_management && (
                      <button
                        onClick={() => handleRelease(file)}
                        disabled={
                          releasingFile === file.filename ||
                          taskType !== "PREPARE"
                        }
                      >
                        {releasingFile === file.filename
                          ? "Releasing..."
                          : "Release"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
      ) : (
        <p>No files available.</p>
      )}

      {files.length > 1 && (
        <button
          onClick={() => setShowMore(!showMore)}
          className="show-more-btn"
        >
          {showMore
            ? "▲ Show Less"
            : `▼ Show ${files.length - 1} more file${files.length - 1 > 1 ? "s" : ""}`}
        </button>
      )}
    </>
  );

  return (
    <div className="company-card">
      <div className="company-info">
        <h3>
          {displayName || name} ({ticker})
        </h3>
        {!benchmark && (
          <button
            onClick={handleInside}
            className="inside-btn"
            disabled={addingInsider}
          >
            {creatingFile ? "Sending..." : "Register Inside Information"}
          </button>
        )}
        {!benchmark && (
          <button
            onClick={handleCreate}
            className="create-btn"
            disabled={creatingFile}
          >
            {creatingFile ? "Creating..." : "+ Create"}
          </button>
        )}
        {user?.is_management && benchmark && (
          <button
            onClick={handleNewCreate}
            className="create-btn"
            disabled={creatingNewFile}
          >
            {creatingNewFile ? "Creating..." : "New Create"}
          </button>
        )}
        {user?.is_management && benchmark && (
          <button
            onClick={() => setAdvancedVisible(true)}
            className="create-btn"
          >
            Advanced
          </button>
        )}
        {user?.is_management && !benchmark && (
          <button
            onClick={handleNewRefresh}
            className="create-btn"
            disabled={newRefreshSubmitting}
          >
            New Refresh
          </button>
        )}
        {user?.is_management && !benchmark && (
          <button
            onClick={() => {
              const saved = localStorage.getItem(`ai_guidance_${ticker}`);
              setAiGuidance(saved ? JSON.parse(saved) : defaultGuidance);
              setAiGuidanceModalVisible(true);
            }}
            className="create-btn"
          >
            Create AI
          </button>
        )}
      </div>

      {model && model.filename && model.last_modified && (
        <div className="model-info">
          <p>
            <strong>Model last updated: </strong>
            {formatDate(model.last_modified)} ({timeAgo(model.last_modified)})
          </p>
        </div>
      )}

      {renderFiles()}

      {advancedVisible && (
        <div className="modal-overlay">
          <div className="modal-box-advanced">
            <div className="advanced-header">
              <h3>
                {displayName || name} ({ticker})
              </h3>
              <button className="cancel-btn" onClick={closeAdvanced}>
                Close
              </button>
            </div>
            <div className="advanced-body">
              <div className="advanced-overview">
                {overviewError ? (
                  <p className="form-error">{overviewError}</p>
                ) : !overview ? (
                  <p className="advanced-loading">Loading share price...</p>
                ) : (
                  <>
                    <dl className="overview-stats">
                      <div>
                        <dt>Share price</dt>
                        <dd>
                          {formatMoney(overview.share_price, overview.trading_currency)}
                          {overview.trading_currency.code && (
                            <small className="overview-currency">{overview.trading_currency.code}</small>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Market cap</dt>
                        <dd>{formatMoney(overview.mktcap, overview.valuation_currency, { millions: true })}</dd>
                      </div>
                    </dl>
                    <div className="overview-chart">
                      {overview.prices === null ? (
                        <>
                          <div className="overview-chart-title">{chartTitle}</div>
                          <p className="form-error">Couldn't load the share price history.</p>
                        </>
                      ) : (
                        <SharePriceChart
                          title={chartTitle}
                          prices={overview.prices}
                          formatPrice={(price) => formatMoney(price, overview.trading_currency)}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
              <FinancialsGrid
                companyId={companyId}
                table={financialsTable}
                financials={financials}
                yearEnd={yearEnd}
                onSaved={fetchCompanies}
                onDirtyChange={setFinancialsDirty}
              />
              <div className="advanced-files-heading">
                <h4>Files</h4>
                <button
                  onClick={handleNewCreate}
                  className="create-btn"
                  disabled={creatingNewFile}
                >
                  {creatingNewFile ? "Creating..." : "New Create"}
                </button>
              </div>
              {renderFiles()}
            </div>
          </div>
        </div>
      )}

      {createModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <p>
              About to create a new note for <strong>{displayName || name}</strong>.
            </p>
            <p>
              <strong>Model last updated:</strong>{" "}
              {formatDate(model.last_modified)}.
            </p>

            <div className="question-section">
              <label>
                <input
                  type="checkbox"
                  checked={includeQuestion}
                  onChange={() => setIncludeQuestion(!includeQuestion)}
                />
                <strong>
                  {" "}
                  Have you received inside information for this company (and
                  haven't already been made inside)?{" "}
                </strong>
              </label>
            </div>

            {includeQuestion && (
              <div className="question-form">
                <label>Information received from:</label>
                <input
                  type="text"
                  value={addBy}
                  onChange={(e) => setAddBy(e.target.value)}
                  placeholder="Full name"
                />

                <label>Added on date/time:</label>
                <input
                  type="datetime-local"
                  value={addTime}
                  onChange={(e) => setAddTime(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setCreateModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmCreate}
                disabled={includeQuestion && (!addBy || !addTime)} // Disable if any field is empty
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {refreshModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                About to refresh note <strong>{selectedFile.filename}</strong>.
              </p>
              <p>
                <strong>Document last saved:</strong>{" "}
                {formatDate(selectedFile.last_modified)} (
                {timeAgo(selectedFile.last_modified)}).
              </p>
              {!benchmark && (
                <p>
                  <strong>Model last updated:</strong>{" "}
                  {formatDate(model.last_modified)} (
                  {timeAgo(model.last_modified)}).
                </p>
              )}
              {benchmark && renderRefreshFinancialsOption()}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setRefreshModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmRefresh}
                disabled={benchmark && refreshFinancialsBlocked}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {newCreateModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                About to create a new note for <strong>{displayName || name}</strong>.
              </p>
              <div className="question-form">
                <label>Forecast:</label>
                <select
                  value={newCreateForecast}
                  onChange={(e) => setNewCreateForecast(e.target.value)}
                >
                  <option value="one_year">One Year Forecast</option>
                  <option value="two_years">Two Years Forecast</option>
                </select>
                {newCreateShortBy > 0 && (
                  <small className="form-hint">
                    The financials table will be left blank: a{" "}
                    {newCreateForecast === "one_year" ? "one year" : "two years"}{" "}
                    forecast needs {REQUIRED_SELECTED[newCreateForecast]} periods
                    selected in Advanced ({selectedPeriods} selected).
                  </small>
                )}
                {financialsDirty && (
                  <small className="form-error">
                    Save your changes to the financials table first.
                  </small>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setNewCreateModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmNewCreate}
                disabled={creatingNewFile || financialsDirty}
              >
                {creatingNewFile ? "Creating..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {newRefreshModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                About to refresh a New Create note for <strong>{displayName || name}</strong>.
              </p>
              {benchmarkFiles.length > 0 ? (
                <div className="question-form">
                  <label>File to refresh:</label>
                  <select
                    value={newRefreshSelectedFile}
                    onChange={(e) => setNewRefreshSelectedFile(e.target.value)}
                  >
                    {benchmarkFiles.map((file) => (
                      <option key={file.filename} value={file.filename}>
                        {file.filename} ({timeAgo(file.last_modified)})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p>No New Create files found for {ticker}.</p>
              )}
              {renderRefreshFinancialsOption()}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setNewRefreshModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmNewRefresh}
                disabled={
                  !newRefreshSelectedFile ||
                  newRefreshSubmitting ||
                  refreshFinancialsBlocked
                }
              >
                {newRefreshSubmitting ? "Refreshing..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                About to check note <strong>{selectedFile.filename}</strong>.
              </p>
              <p>
                <strong>Document last saved:</strong>{" "}
                {formatDate(selectedFile.last_modified)} (
                {timeAgo(selectedFile.last_modified)}).
              </p>
              <p>
                <strong>Model last updated:</strong>{" "}
                {formatDate(model.last_modified)} (
                {timeAgo(model.last_modified)}).
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setCheckModalVisible(false)}
              >
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmCheck}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {prepareModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                About to prepare note <strong>{selectedFile.filename}</strong>.
              </p>
              <p>
                <strong>Document last saved:</strong>{" "}
                {formatDate(selectedFile.last_modified)} (
                {timeAgo(selectedFile.last_modified)}).
              </p>
              {!benchmark && (
                <p>
                  <strong>Model last updated:</strong>{" "}
                  {formatDate(model.last_modified)} (
                  {timeAgo(model.last_modified)}).
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setPrepareModalVisible(false)}
              >
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmPrepare}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {releaseModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div>
              <p>
                <strong>ABOUT TO RELEASE NOTE {selectedFile.filename}.</strong>
              </p>
              <p>
                <strong>Document last saved:</strong>{" "}
                {formatDate(selectedFile.last_modified)} (
                {timeAgo(selectedFile.last_modified)}).
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setReleaseModalVisible(false)}
              >
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmRelease}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {aiGuidanceModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box-wide">
            <h3>Create AI Note — {displayName || name}</h3>
            <div>
              <label>
                <strong>General guidance</strong>{" "}
                <small style={{ fontWeight: "normal", color: "#666" }}>
                  — applies across all sections
                </small>
              </label>
              <textarea
                name="general"
                rows={3}
                value={aiGuidance.general}
                onChange={handleGuidanceChange}
                placeholder={
                  aiPromptFields.general ||
                  "e.g. The company beat consensus on revenue but missed on EBITDA. Focus on the software division."
                }
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  resize: "vertical",
                  marginTop: "4px",
                }}
              />
            </div>
            <hr style={{ margin: "4px 0", borderColor: "#eee" }} />
            {[
              {
                key: "title",
                label: "Title",
                placeholder: "e.g. Strong results with record revenue",
              },
              {
                key: "boldpara",
                label: "Bold Paragraph",
                placeholder:
                  "e.g. Emphasise the net cash position and beat vs consensus",
              },
              {
                key: "bullet1",
                label: "Section 1",
                placeholder:
                  "e.g. Highlight the new contract with XYZ signed in March",
              },
              {
                key: "bullet2",
                label: "Section 2",
                placeholder: "e.g. Note the weakness in the legacy division",
              },
              {
                key: "bullet3",
                label: "Section 3",
                placeholder: "e.g. Mention the upgraded revenue guidance",
              },
              {
                key: "bullet4",
                label: "Section 4",
                placeholder: "e.g. Comment on the discount to peers",
              },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label>
                  <strong>{label}</strong>
                </label>
                <textarea
                  name={key}
                  rows={2}
                  value={aiGuidance[key]}
                  onChange={handleGuidanceChange}
                  placeholder={aiPromptFields[key] || placeholder}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "vertical",
                    marginTop: "4px",
                  }}
                />
              </div>
            ))}
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setAiGuidanceModalVisible(false)}
                disabled={aiLoading}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleCreateAI}
                disabled={aiLoading}
              >
                {aiLoading ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {aiModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box-wide">
            <h3>Review AI Content — {displayName || name}</h3>
            {aiContent.comment && aiContent.comment.trim() && (
              <p
                style={{
                  fontStyle: "italic",
                  color: "#555",
                  background: "#f7f7f7",
                  padding: "8px 12px",
                  borderRadius: "4px",
                }}
              >
                {aiContent.comment}
              </p>
            )}
            {[
              { key: "title", label: "Title", rows: 2 },
              { key: "boldpara", label: "Bold Paragraph", rows: 5 },
              { key: "bullet1", label: "Section 1", rows: 4 },
              { key: "bullet2", label: "Section 2", rows: 4 },
              { key: "bullet3", label: "Section 3", rows: 4 },
              { key: "bullet4", label: "Section 4", rows: 4 },
            ].map(({ key, label, rows }) => (
              <div key={key} style={{ marginBottom: "10px" }}>
                <label>
                  <strong>{label}</strong>
                </label>
                <textarea
                  name={key}
                  rows={rows}
                  value={aiContent[key]}
                  onChange={handleAiChange}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                />
              </div>
            ))}
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setAiModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="cancel-btn"
                onClick={() => {
                  setAiModalVisible(false);
                  const saved = localStorage.getItem(`ai_guidance_${ticker}`);
                  setAiGuidance(saved ? JSON.parse(saved) : defaultGuidance);
                  setAiGuidanceModalVisible(true);
                }}
              >
                Try Again
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmCreateAI}
                disabled={aiSubmitting}
              >
                {aiSubmitting ? "Creating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {insideModalVisible && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="question-section">
              <strong> Have you received inside information for {displayName || name}?</strong>
            </div>

            <div className="question-form">
              <label>Information received from:</label>
              <input
                type="text"
                value={addBy}
                onChange={(e) => setAddBy(e.target.value)}
                placeholder="Enter name"
                required
              />
              <label>Added on date/time:</label>
              <input
                type="datetime-local"
                value={addTime}
                onChange={(e) => setAddTime(e.target.value)}
                required
              />
            </div>

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setInsideModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmInside}
                disabled={!addBy || !addTime} // Disable if any field is empty
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCard;
